import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SignUp.css';

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    password: '',
    confirmPassword: '',
    riskProfile: '',
    referral: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const topBarRef = useRef(null);

  // Language toggle handler
  const handleLanguageToggle = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Scroll handler for top bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('signup.form.passwordMismatch') || 'Passwords do not match');
      return;
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // Validate required fields
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      return;
    }
    
    setIsLoading(true);
    
    const result = await signup(
      formData.email,
      formData.password,
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        organization: formData.organization,
        riskProfile: formData.riskProfile || 'Neutral'
      }
    );
    
    if (result.success) {
      // Show success message and redirect to sign in
      // User was signed out after signup to require email verification
      alert('Account created successfully! Please check your email to verify your account before signing in.');
      // Redirect to sign in page
      navigate('/signin');
    } else {
      setError(result.error || 'Sign up failed');
    }
    
    setIsLoading(false);
  };

  // Modal handlers
  const openTermsModal = () => setOpenModal('terms');
  const openPrivacyModal = () => setOpenModal('privacy');
  const closeModal = () => setOpenModal(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && openModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [openModal]);

  // Get current year for footer
  const currentYear = new Date().getFullYear();

  return (
    <div className="signup-wrapper">
      {/* Fixed Top Bar */}
      <nav 
        ref={topBarRef}
        className={`fixed-top-bar ${isScrolled ? 'scrolled' : ''}`}
      >
        <div className="top-bar-logo">
          <button
            className="language-toggle"
            aria-label={t('signup.topBar.toggleLanguage')}
            onClick={(e) => {
              const option = e.target.closest('.lang-option');
              if (option) {
                const targetLang = option.dataset.lang;
                if (targetLang) {
                  handleLanguageToggle(targetLang);
                }
              }
            }}
          >
            <span 
              className={`lang-option ${i18n.language === 'en' ? 'active' : ''}`} 
              data-lang="en"
            >
              EN
            </span>
            <span className="lang-separator">|</span>
            <span 
              className={`lang-option ${i18n.language === 'es' ? 'active' : ''}`} 
              data-lang="es"
            >
              ES
            </span>
          </button>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="86.609"
            height="77.109"
            viewBox="0 0 86.609 77.109"
          >
            <defs>
              <clipPath id={`clip-path-signup-${i18n.language}`}>
                <rect
                  width="42.305"
                  height="48.895"
                  transform="translate(-3 -6)"
                  fill="none"
                  stroke="#49a9ff"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </clipPath>
            </defs>
            <path
              d="M31.061,4.859,14.374,37.091.435,65.389c-1.175,2.385.114,4.365,2.866,4.4l27.76.34,27.76-.34c2.752-.034,4.041-2.013,2.866-4.4l-13.94-28.3Z"
              transform="translate(0 6.982)"
              fill="#49a9ff"
            />
            <g transform="translate(48.422 6)" style={{ isolation: 'isolate' }}>
              <g transform="translate(-1.119 0)">
                <g clipPath={`url(#clip-path-signup-${i18n.language})`}>
                  <path
                    d="M45.006,46.045,54.8,27.824l8.179-15.995a1.754,1.754,0,0,0-1.683-2.488L45.006,9.15l-16.289.191a1.754,1.754,0,0,0-1.681,2.488l8.179,15.995Z"
                    transform="translate(-26.854 -9.149)"
                    fill="none"
                    stroke="#49a9ff"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </g>
              </g>
            </g>
          </svg>
        </div>
        <div className="top-bar-buttons">
          <Link 
            to="/landing" 
            className="top-bar-button tertiary"
          >
            {t('signup.topBar.overview')}
          </Link>
          <Link 
            to="/signin" 
            className="top-bar-button secondary"
          >
            {t('signup.topBar.signIn')}
          </Link>
          <Link 
            to="/subscriptions" 
            className="top-bar-button primary"
          >
            {t('signup.topBar.viewPlans')}
          </Link>
        </div>
      </nav>

      {/* Auth Section */}
      <section className="auth-section">
        <article className="auth-card">
          <div className="auth-info">
            <img 
              src="/images/logos/logo.svg" 
              alt={t('signup.authInfo.logoAlt')} 
              className="auth-info-logo" 
            />
            <p 
              className="eyebrow" 
              style={{
                letterSpacing: '0.4em',
                textTransform: 'uppercase',
                color: 'rgba(244, 247, 251, 0.7)',
                margin: 0
              }}
            >
              {t('signup.authInfo.eyebrow')}
            </p>
            <h1>
              {t('signup.authInfo.heading.prefix')}{' '}
              <span>{t('signup.authInfo.heading.company')}</span>
            </h1>
            <p>{t('signup.authInfo.description')}</p>
            <div className="onboarding-steps">
              {t('signup.authInfo.onboardingSteps', { returnObjects: true }).map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="auth-form">
            <div>
              <h2 className="form-title">{t('signup.form.title')}</h2>
              <p className="form-subtitle">{t('signup.form.subtitle')}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="firstName">{t('signup.form.firstNameLabel')}</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder={t('signup.form.firstNamePlaceholder')}
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">{t('signup.form.lastNameLabel')}</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder={t('signup.form.lastNamePlaceholder')}
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t('signup.form.emailLabel')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={t('signup.form.emailPlaceholder')}
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="organization">{t('signup.form.organizationLabel')}</label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    placeholder={t('signup.form.organizationPlaceholder')}
                    autoComplete="organization"
                    value={formData.organization}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">{t('signup.form.passwordLabel')}</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder={t('signup.form.passwordPlaceholder')}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">{t('signup.form.confirmPasswordLabel')}</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder={t('signup.form.confirmPasswordPlaceholder')}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="riskProfile">{t('signup.form.riskProfileLabel')}</label>
                  <select
                    id="riskProfile"
                    name="riskProfile"
                    value={formData.riskProfile}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>{t('signup.form.riskProfilePlaceholder')}</option>
                    <option value="averse">{t('signup.form.riskProfileOptions.averse')}</option>
                    <option value="neutral">{t('signup.form.riskProfileOptions.neutral')}</option>
                    <option value="seeker">{t('signup.form.riskProfileOptions.seeker')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="referral">{t('signup.form.referralLabel')}</label>
                  <input
                    type="text"
                    id="referral"
                    name="referral"
                    placeholder={t('signup.form.referralPlaceholder')}
                    value={formData.referral}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              {error && (
                <div style={{
                  color: '#ff6b6b',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginTop: '16px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginTop: '32px' }}>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={isLoading}
                  style={{ 
                    opacity: isLoading ? 0.6 : 1, 
                    cursor: isLoading ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {isLoading ? 'Creating account...' : t('signup.form.submitButton')}
                </button>
              </div>
            </form>
            <p className="compliance-note">
              {t('signup.form.compliance.prefix')}{' '}
              <button
                type="button"
                onClick={openTermsModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--accent)', 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {t('signup.form.compliance.terms')}
              </button>{' '}
              {t('signup.form.compliance.and')}{' '}
              <button
                type="button"
                onClick={openPrivacyModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--accent)', 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                {t('signup.form.compliance.privacy')}
              </button>{' '}
              {t('signup.form.compliance.suffix')}
            </p>
            <p className="auth-meta">
              {t('signup.form.meta.prefix')}{' '}
              <Link to="/signin">
                {t('signup.form.meta.link')}
              </Link>
            </p>
          </div>
        </article>
      </section>

      {/* Footer */}
      <footer className="signup-footer">
        &copy; {currentYear} {t('signup.footer.copyright')}
        <br />
        {t('signup.footer.helpPrefix')}{' '}
        <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>
      </footer>

      {/* Terms of Service Modal */}
      {openModal === 'terms' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-modal="true" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.classList.contains('modal-backdrop') || e.target.classList.contains('modal')) {
              closeModal();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="modal-container">
            <button 
              className="modal-close" 
              aria-label={t('signup.modals.close')}
              onClick={closeModal}
            >
              &times;
            </button>
            <div className="modal-content">
              <h2>{t('signup.modals.terms.title')}</h2>
              <p className="modal-updated">{t('signup.modals.terms.updated')}</p>
              {t('signup.modals.terms.sections', { returnObjects: true }).map((section, index) => (
                <div key={index}>
                  <h3>{section.title}</h3>
                  {Array.isArray(section.content) ? (
                    <ul>
                      {section.content.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{section.content}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {openModal === 'privacy' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-modal="true" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.classList.contains('modal-backdrop') || e.target.classList.contains('modal')) {
              closeModal();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="modal-container">
            <button 
              className="modal-close" 
              aria-label={t('signup.modals.close')}
              onClick={closeModal}
            >
              &times;
            </button>
            <div className="modal-content">
              <h2>{t('signup.modals.privacy.title')}</h2>
              <p className="modal-updated">{t('signup.modals.privacy.updated')}</p>
              <p>{t('signup.modals.privacy.intro')}</p>
              {t('signup.modals.privacy.sections', { returnObjects: true }).map((section, index) => (
                <div key={index}>
                  <h3>{section.title}</h3>
                  {Array.isArray(section.content) ? (
                    <ul>
                      {section.content.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{section.content}</p>
                  )}
                </div>
              ))}
              <p>{t('signup.modals.privacy.conclusion')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;




